// The claim handoff on /welcome, taken off the URL before analytics exists
// (TAS-373 review).
//
// "Save my score" and prize sign-in links land on
//   /welcome?code=<session>&player=<uuid>&ct=<claim token>
// `ct` is a bearer capability: whoever holds it can act as that player. Left in
// the address bar it rides into Mixpanel's automatic $current_url, session
// replay, logged `location.search` and browser history; `player` is a uuid that
// has no business there either. WelcomePage used to strip `ct` in a mount
// effect of a lazy route — long after the first event.
//
// So this runs at module load of analytics-logger, BEFORE mixpanel.init, and
// again from the game's main.jsx before the router is created (idempotent). The
// values move to sessionStorage — this tab only — where WelcomePage reads them
// first. No dependencies, so importing it can never reorder anything.

const KEY = 'tasted:welcome-handoff';

function onWelcome(pathname) {
  return String(pathname || '').replace(/\/+$/, '') === '/welcome';
}

function store() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The handoff captured this tab, or null. */
export function readClaimHandoff() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = store()?.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Forget it once it has done its job. */
export function clearClaimHandoff() {
  try {
    store()?.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Lift `ct` off any URL, and `player` + `code` off /welcome, into sessionStorage.
 * `code` is only taken alongside `player` — that pair is ours; a lone `code`
 * could be something else's.
 *
 * @returns {{code?: string, player?: string, ct?: string} | null} what is stored
 */
export function captureClaimHandoff() {
  if (typeof window === 'undefined' || !window.history?.replaceState) return null;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const welcome = onWelcome(url.pathname);
    const ct = params.get('ct');
    const player = welcome ? params.get('player') : null;
    const code = welcome && player ? params.get('code') : null;
    if (ct === null && player === null) return readClaimHandoff();

    const handoff = {};
    if (code) handoff.code = code;
    if (player) handoff.player = player;
    if (ct) handoff.ct = ct;
    try {
      store()?.setItem(KEY, JSON.stringify(handoff));
    } catch {
      /* storage blocked — still strip the URL; the capability must not leak */
    }

    params.delete('ct');
    if (player !== null) params.delete('player');
    if (code !== null) params.delete('code');
    window.history.replaceState(window.history.state, '', url.toString());
    return handoff;
  } catch {
    return null;
  }
}

// ── Supabase auth fragment ──────────────────────────────────────────────────
//
// A magic link lands as #access_token=…&refresh_token=…&type=magiclink. Those
// are live credentials. Sanitising the `hash` property we pass to analytics is
// not enough: Mixpanel stamps its OWN $current_url on every event, read from
// window.location.href at send time, and session replay records the address
// bar too. mixpanel-browser 2.63.0 has no sanitize_properties hook to
// intercept that, so the only place this can be fixed once and for all is the
// URL itself — before mixpanel.init() runs.
//
// So this is captured and stripped at module load of analytics-logger, exactly
// like the /welcome claim handoff above, and consumeMagicLinkHash() reads it
// back out of here instead of off the URL.
let _authFragment = null;

/**
 * Move a Supabase auth fragment off the URL and into memory.
 * Call BEFORE mixpanel.init(). Idempotent and safe on a URL without one.
 * @returns {string|null} the raw fragment (no leading '#'), or null
 */
export function captureAuthFragment() {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const raw = (window.location.hash || '').replace(/^#/, '');
    // No '=' means a plain anchor like #boxes: never a grant, leave it alone.
    if (!raw || !raw.includes('=')) return null;
    const params = new URLSearchParams(raw);
    // error/error_description are not credentials, but the sign-in pages need
    // them to explain an expired link, so they travel by the same route.
    const isAuth =
      params.has('access_token') ||
      params.has('refresh_token') ||
      params.has('error_description') ||
      params.has('error');
    if (!isAuth) return null;
    _authFragment = raw;
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    );
    return raw;
  } catch {
    return null;
  }
}

/**
 * Read the captured auth fragment and forget it, so a second call finds
 * nothing — the idempotence consumeMagicLinkHash() documents.
 * @returns {string|null}
 */
export function takeAuthFragment() {
  const raw = _authFragment;
  _authFragment = null;
  return raw;
}

/**
 * Read it without consuming it, for the pages that used to read
 * window.location.hash directly (the game's /welcome, admin's password
 * reset). Reading the URL was never destructive, so neither is this: only
 * consumeMagicLinkHash() clears the stash, exactly as only it used to strip
 * the URL.
 * @returns {string|null}
 */
export function peekAuthFragment() {
  return _authFragment;
}

const NEVER_LOG = ['ct', 'player', 't', 'email', 'mp_id'];

/**
 * A search string safe to log or attach to an event: the capability, the
 * player id, the attribution token and the scrubbed identifiers removed.
 */
export function safeSearch(search) {
  if (!search) return search;
  try {
    const params = new URLSearchParams(search);
    for (const key of NEVER_LOG) params.delete(key);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  } catch {
    return '';
  }
}

// A Supabase implicit-grant magic link lands as
// #access_token=…&refresh_token=…&type=magiclink. Those are live credentials:
// anyone holding them is signed in as that person. sanitizeUrl() in
// analytics-logger strips the fragment from `url` and `referrer` (TAS-92), but
// a fragment passed as its own event property never goes through it.
const NEVER_LOG_FRAGMENT = [
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'token',
  ...NEVER_LOG,
];

/**
 * A URL fragment safe to log or attach to an event.
 *
 * An ordinary anchor ('#boxes') is returned untouched. A fragment carrying
 * credentials is replaced wholesale rather than filtered: these arrive as one
 * inseparable grant, and reporting which keys were present is itself a hint.
 *
 * @param {string} hash location.hash, with or without the leading '#'
 * @returns {string} the original fragment, or '#redacted'
 */
const CREDENTIAL_IN_STRING = /(?:access_token|refresh_token|id_token|provider_token)=/i;

/**
 * Router state, safe to log. A protected route redirecting to a sign-in page
 * copies the whole location it came from into `state.from` (cellar does this),
 * which carries that location's fragment past the redirect and into the log.
 * Walks the value, sanitising `search`/`hash` keys and blanking any string
 * that still carries a credential. Depth-bounded: state is caller-supplied.
 */
export function safeState(value, depth = 0) {
  if (value == null || depth > 4) return value;
  if (typeof value === 'string') {
    return CREDENTIAL_IN_STRING.test(value) ? '[redacted]' : value;
  }
  if (Array.isArray(value)) return value.map((v) => safeState(v, depth + 1));
  if (typeof value !== 'object') return value;
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (key === 'hash' && typeof val === 'string') out[key] = safeHash(val);
    else if (key === 'search' && typeof val === 'string') out[key] = safeSearch(val);
    else out[key] = safeState(val, depth + 1);
  }
  return out;
}

export function safeHash(hash) {
  if (!hash) return hash;
  try {
    const raw = String(hash).replace(/^#/, '');
    // No '=' means a plain anchor, which cannot be a credential grant.
    if (!raw || !raw.includes('=')) return hash;
    const params = new URLSearchParams(raw);
    const carriesSecret = NEVER_LOG_FRAGMENT.some((key) => params.has(key));
    return carriesSecret ? '#redacted' : hash;
  } catch {
    // Unparseable and containing '=' — refuse to forward it.
    return '#redacted';
  }
}
